// components/common/ConfigurableList/ConfigurableList.jsx
import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Input,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DataTable from "react-data-table-component";
import { useList } from "../../../hooks/useList";
import { createColumnSet } from "../../../utils/columnUtils";
import { Loader } from "../../CustomLoader";
import TableFilter from "../../shared/TableFilter";

const ConfigurableList = ({ config }) => {
  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  const {
    title,
    fields,
    services,
    filters = [],
    options = {},
    messages = {},
    layout = {},
  } = config;
console.log("ConfigurableList config:", config);
  const {
    enableSearch = true,
    enableFilters = true,
    enablePagination = true,
    defaultPageSize = 10,
    pageSizeOptions = [10, 20, 50],
    searchPlaceholder = "Search",
  } = options;

  const {
    emptyMessage = "No Data Found",
    deleteConfirmMessage = "Are you sure you want to delete this item?",
    blockConfirmMessage = (isBlocked) => 
      `Are you sure you want to ${isBlocked ? "unblock" : "block"} this item?`,
  } = messages;

  const {
    showSerialNumber = true,
    showStatus = true,
    showActions = true,
    basePath = "",
    customActions = [],
  } = layout;

  // Use the custom hook
  const {
    searchItem,
    data,
    totalPages,
    isLoading,
    loaderDelay,
    filterValues,
    handleSearchChange,
    handlePageChange,
    handleFilterChange,
    handleDelete,
    handleBlock,
    handleStatusToggle,
    clearSearch,
    setPageSize,
  } = useList({
    fetchData: services.fetchData,
    deleteData: services.deleteData,
    blockData: services.blockData,
    toggleStatus: services.toggleStatus,
    defaultPageSize,
  });
console.log("showSerialNumber", showSerialNumber);

  // Create columns configuration
  const columns = useMemo(() => 
    createColumnSet({
      basicFields: fields,
      includeSerialNumber: showSerialNumber,
      includeStatus: showStatus,
      includeActions: showActions,
      currentPage,
      basePath,
      actionConfig: {
        actions: layout.actions || {},
        onDelete: (id) => handleDelete(id, deleteConfirmMessage),
        onBlock: (id) => handleBlock(id, blockConfirmMessage),
        onStatusToggle: handleStatusToggle,
        customActions,
      },
    }), 
    [
      fields, 
      showSerialNumber, 
      showStatus, 
      showActions, 
      currentPage, 
      basePath, 
      layout.actions, 
      handleDelete, 
      handleBlock, 
      handleStatusToggle, 
      deleteConfirmMessage, 
      blockConfirmMessage, 
      customActions
    ]
  );

  return (
    <Card>
      <title>{title}</title>
      <CardHeader 
        title={title} 
        action={layout.headerActions}
      />
      
      {(enableFilters || enableSearch) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            px: 2,
            mb: 2,
          }}
        >
          {enableFilters && filters.map((filter) => (
            <TableFilter
              key={filter.key}
              className="filter_option_css"
              data={filter.options}
              value={filterValues[filter.key] || []}
              setValue={(value) => handleFilterChange(filter.key, value)}
              filterName={filter.name}
            />
          ))}

          {enableSearch && (
            <Input
              value={searchItem}
              placeholder={searchPlaceholder}
              onChange={handleSearchChange}
              endAdornment={
                <InputAdornment position="end">
                  {searchItem && (
                    <IconButton
                      aria-label="clear-search"
                      onClick={clearSearch}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </InputAdornment>
              }
            />
          )}
        </Box>
      )}
      
      <CardContent>
        {isLoading ? (
          <Loader />
        ) : data && data.length > 0 ? (
          <DataTable
            customStyles={{
              headCells: {
                style: {
                  fontSize: "15px",
                  fontWeight: "bold",
                },
              },
            }}
            columns={columns}
            data={data}
            pagination={enablePagination}
            paginationServer={enablePagination}
            paginationTotalRows={totalPages}
            onChangePage={handlePageChange}
            paginationDefaultPage={currentPage}
            paginationPerPage={defaultPageSize}
            paginationRowsPerPageOptions={pageSizeOptions}
            onChangeRowsPerPage={(currentRowsPerPage) => {
              setPageSize(currentRowsPerPage);
            }}
            progressPending={isLoading || !loaderDelay}
            progressComponent={<Loader />}
            className="custom"
          />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="150px">
            <p style={{ textAlign: 'center', margin: 0 }}>{emptyMessage}</p>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ConfigurableList;